/**
 * Timezone utilities for the HH frontend.
 *
 * The organisation's IANA timezone is stored in the active OrgMembership and is
 * used wherever we need to display or collect timestamps that originate from—or
 * will be stored in—the UTC database.
 */

import { useAuth } from '../context/AuthContext';
import type { User } from '../types';
import { displayLocale } from '../i18n/locale';

// ─── Helpers to read the org timezone ────────────────────────────────────────

/**
 * Returns the IANA timezone string for the user's currently-active organisation.
 * Falls back to the browser's timezone when no org membership is found.
 */
export function getOrgTimezone(user: User | null): string {
  const activeOrg = user?.orgs?.find(o => o.is_active);
  return activeOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The org's IANA timezone, or null when no active membership carries one.
 *
 * `getOrgTimezone` falls back to the browser's own timezone, which is right for
 * DISPLAY — a date has to render as something — but wrong anywhere the answer
 * must be the same for everyone looking at the same thing. A browser's timezone
 * is a property of the machine, so a page that compares times against it can
 * read one way on a desktop and another way on a phone from identical data.
 * Callers that cannot tolerate that ask for this instead and supply their own
 * fallback. See `siteOffsetSec` in irrigationClock.
 */
export function orgTimezoneOrNull(user: User | null): string | null {
  return user?.orgs?.find(o => o.is_active)?.timezone ?? null;
}

/** Hook form of {@link orgTimezoneOrNull}. */
export function useOrgTimezoneOrNull(): string | null {
  const { user } = useAuth();
  return orgTimezoneOrNull(user);
}

/**
 * Hook – returns the IANA timezone for the current user's active organisation.
 */
export function useOrgTimezone(): string {
  const { user } = useAuth();
  return getOrgTimezone(user);
}

// ─── Display formatters ───────────────────────────────────────────────────────

/**
 * Formats a UTC ISO timestamp as a time string in the given IANA timezone.
 *
 * @example
 *   formatTimeInTz('2024-03-16T02:30:00.000Z', 'Australia/Melbourne')
 *   // → "1:30 PM" (AEDT = UTC+11)
 */
export function formatTimeInTz(isoString: string, timezone: string): string {
  // eslint-disable-next-line no-restricted-syntax
  return new Date(isoString).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });
}

/**
 * Formats a UTC ISO timestamp as a date string in the given IANA timezone.
 * Use this for full datetime columns (e.g. clock_in_time, created_at).
 *
 * @example
 *   formatDateInTz('2024-03-16T02:30:00.000Z', 'Australia/Melbourne')
 *   // → "16 Mar 2024"
 *
 *   formatDateInTz('2024-03-16T02:30:00.000Z', 'Australia/Melbourne', { weekday: 'short', day: 'numeric', month: 'short' })
 *   // → "Sat, 16 Mar"
 */
export function formatDateInTz(
  isoString: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  // eslint-disable-next-line no-restricted-syntax
  return new Date(isoString).toLocaleDateString(displayLocale(), {
    ...options,
    timeZone: timezone,
  });
}

/**
 * Formats a plain YYYY-MM-DD date string (no time component) in the given
 * IANA timezone. Use this for DATE-only database columns (e.g. timesheet.date,
 * period_start, period_end) to avoid browser-timezone drift.
 *
 * Uses noon UTC as the anchor so the correct calendar day is preserved for
 * any org timezone (UTC±14). Unlike formatDateInTz, this does NOT interpret
 * the string as a UTC timestamp — it treats it as a local calendar date.
 *
 * The default format matches formatDateInTz (day + month + year, no weekday).
 * Pass a custom options object to include weekday or suppress year.
 *
 * @example
 *   formatLocalDateInTz('2026-03-26', 'Australia/Melbourne')
 *   // → "26 Mar 2026"
 *
 *   formatLocalDateInTz('2026-03-26', 'Australia/Melbourne', { weekday: 'short', day: 'numeric', month: 'short' })
 *   // → "Thu 26 Mar"
 */
export function formatLocalDateInTz(
  dateStr: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  // eslint-disable-next-line no-restricted-syntax
  return new Date(dateStr + 'T12:00:00Z').toLocaleDateString(displayLocale(), {
    ...options,
    timeZone: timezone,
  });
}

/**
 * Formats a UTC ISO timestamp as a full date+time string in the given IANA timezone.
 * Use this instead of `new Date(x).toLocaleString()` which uses the browser timezone.
 *
 * @example
 *   formatDateTimeInTz('2024-03-16T02:30:00.000Z', 'Australia/Melbourne')
 *   // → "16 Mar 2024, 1:30 PM"
 */
export function formatDateTimeInTz(isoString: string, timezone: string): string {
  return new Date(isoString).toLocaleString(displayLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });
}

// ─── Input value helpers ──────────────────────────────────────────────────────

/**
 * Converts a UTC ISO timestamp to the `YYYY-MM-DDTHH:mm` string expected by
 * `<input type="datetime-local">`, expressed in the given IANA timezone.
 *
 * @example
 *   formatDateTimeForInput('2024-03-16T02:30:00.000Z', 'Australia/Melbourne')
 *   // → "2024-03-16T13:30"  (AEDT = UTC+11)
 */
export function formatDateTimeForInput(isoString: string, timezone: string): string {
  const date = new Date(isoString);

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const getPartValue = (type: string) => parts.find(p => p.type === type)?.value ?? '00';

  const year = getPartValue('year');
  const month = getPartValue('month');
  const day = getPartValue('day');
  // hour12: false can produce "24" for midnight — normalise to "00"
  const hour = getPartValue('hour') === '24' ? '00' : getPartValue('hour');
  const minute = getPartValue('minute');

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Converts a `<input type="datetime-local">` value (YYYY-MM-DDTHH:mm, interpreted
 * as a local time in `timezone`) back to a UTC ISO string.
 *
 * Uses a two-pass Intl-based offset calculation that correctly handles DST.
 *
 * @example
 *   dateTimeLocalToISO('2024-03-16T13:30', 'Australia/Melbourne')
 *   // → "2024-03-16T02:30:00.000Z"  (AEDT = UTC+11)
 */
export function dateTimeLocalToISO(localDatetime: string, timezone: string): string {
  const [datePart, timePart] = localDatetime.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Step 1 – Create a UTC timestamp that numerically matches the input
  //          (i.e. treat the given YYYY-MM-DDTHH:mm as if it were UTC).
  const utcBase = Date.UTC(year, month - 1, day, hours, minutes, 0);
  const d = new Date(utcBase);

  // Step 2 – Ask Intl what local date/time the `timezone` zone shows for
  //          that UTC instant.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const getPartValue = (type: string) => parseInt(parts.find(p => p.type === type)?.value ?? '0');
  const tzH = getPartValue('hour') % 24; // normalise "24" → 0
  const tzMs = Date.UTC(getPartValue('year'), getPartValue('month') - 1, getPartValue('day'), tzH, getPartValue('minute'), getPartValue('second'));

  // Step 3 – The offset is how many ms UTC is ahead of the timezone representation.
  //          Apply it to get the actual UTC instant for the requested local time.
  const offsetMs = utcBase - tzMs;
  return new Date(utcBase + offsetMs).toISOString();
}

// ─── Schedule time helpers (seconds from UTC midnight) ───────────────────────

/**
 * Converts UTC seconds-from-midnight to a local HH:MM string in the given
 * IANA timezone. Used to display device schedule times in the user's local time.
 *
 * @example
 *   utcSecToLocalHHMM(21600, 'Australia/Melbourne')
 *   // 21600 = 06:00 UTC = 16:00 AEST → "16:00"
 */
export function utcSecToLocalHHMM(utcSec: number, timezone: string): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  const local = new Date(d.getTime() + utcSec * 1000);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(local);
  const h = parts.find(p => p.type === 'hour')?.value ?? '00';
  const m = parts.find(p => p.type === 'minute')?.value ?? '00';
  return `${h === '24' ? '00' : h}:${m}`;
}

/**
 * Converts a local HH:MM string (in the given IANA timezone) to UTC
 * seconds-from-midnight. Used when saving schedule times so devices (which
 * run UTC) receive the correct value.
 *
 * @example
 *   localHHMMToUtcSec('16:00', 'Australia/Melbourne')
 *   // 16:00 AEST = 06:00 UTC → 21600
 */
export function localHHMMToUtcSec(hhmm: string, timezone: string): number {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return NaN;
  const todayStr = getTodayInTz(timezone);
  const isoStr = dateTimeLocalToISO(`${todayStr}T${hhmm}`, timezone);
  const d = new Date(isoStr);
  return ((d.getUTCHours() * 3600 + d.getUTCMinutes() * 60) + 86400) % 86400;
}

// ─── Current-date helpers ─────────────────────────────────────────────────────

/**
 * Returns today's day-of-week in the API format: 1=Mon … 7=Sun.
 * Uses the given IANA timezone so the value is correct for the organisation's
 * locale rather than the browser's local time.
 */
export function getTodayDowInTz(timezone: string): number {
  const weekday = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'long',
  }).format(new Date());
  const map: Record<string, number> = {
    Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4,
    Friday: 5, Saturday: 6, Sunday: 7,
  };
  return map[weekday] ?? 1;
}

/**
 * The offset from UTC, in seconds, that `timezone` is running at `at`.
 *
 * Positive east of Greenwich: Australia/Melbourne returns 36000 in winter and
 * 39600 once daylight saving starts. Derived from Intl rather than from a
 * table, so the DST changeover is handled by the platform and a date in
 * October gets October's answer.
 *
 * WHY THIS IS NEEDED
 *   Device schedule times are stored as seconds from midnight UTC while
 *   `days_of_week` is in LOCAL days — local weekday plus UTC time of day. That
 *   pairing is correct and deliberate (the ESP-NOW controllers run their clocks
 *   in UTC), but it means the two halves disagree about where a day starts, and
 *   anything asking "what is still to come TODAY" has to fold both into one
 *   frame before comparing. See localDaySec in utils/irrigationRuns.ts.
 */
export function tzOffsetSec(timezone: string, at: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);

  const get = (type: string): number => Number(parts.find(p => p.type === type)?.value ?? 0);
  // en-CA with hour12:false spells midnight "24" on some engines; Date.UTC
  // would roll that into the next day and report an offset a day out.
  const hour = get('hour') === 24 ? 0 : get('hour');

  const asIfUtc = Date.UTC(
    get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second')
  );
  // The parts carry no milliseconds, so compare against `at` cut to its whole
  // second. Rounding instead came back a second short whenever the clock read
  // .500 or later, and a schedule start drew as 04:59 for 05:00 half the time.
  return (asIfUtc - Math.floor(at.getTime() / 1000) * 1000) / 1000;
}

/**
 * Returns today's date as a YYYY-MM-DD string in the given IANA timezone.
 */
export function getTodayInTz(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Returns the first day of the current month as a YYYY-MM-DD string in the
 * given IANA timezone.
 */
export function getFirstDayOfMonthInTz(timezone: string): string {
  const today = getTodayInTz(timezone);
  return today.substring(0, 7) + '-01';
}

/**
 * Calculates the start and end dates of the current pay period.
 *
 * @param payCycleStartDay  - API format: 1=Mon, 2=Tue, …, 7=Sun
 * @param payFrequency      - 'weekly' | 'fortnightly'
 * @param orgTimezone       - IANA timezone string
 * @param fortnightlyRefDate - optional YYYY-MM-DD anchor for fortnightly cycles
 * @returns { startDate, endDate } as YYYY-MM-DD strings
 */
export function getCurrentPayPeriod(
  payCycleStartDay: number,
  payFrequency: 'weekly' | 'fortnightly',
  orgTimezone: string,
  fortnightlyRefDate?: string | null,
): { startDate: string; endDate: string } {
  // Get today's date as YYYY-MM-DD in the org timezone, then work in UTC-midnight
  // arithmetic to avoid DST complications.
  const todayStr = getTodayInTz(orgTimezone);
  const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
  const today = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay));

  // Convert API day format (1=Mon … 7=Sun) to JS UTC day (0=Sun … 6=Sat)
  const jsStartDay = payCycleStartDay === 7 ? 0 : payCycleStartDay;

  // Days to go back from today to reach the most recent pay-cycle start day
  const daysBack = (today.getUTCDay() - jsStartDay + 7) % 7;

  // Start of the current pay week (midnight UTC on the correct day)
  const weekStart = new Date(today);
  weekStart.setUTCDate(today.getUTCDate() - daysBack);

  if (payFrequency === 'weekly') {
    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
    return {
      startDate: weekStart.toISOString().slice(0, 10),
      endDate: weekEnd.toISOString().slice(0, 10),
    };
  }

  // ── Fortnightly ──────────────────────────────────────────────────────────
  if (fortnightlyRefDate) {
    const [refYear, refMonth, refDay] = fortnightlyRefDate.split('-').map(Number);
    const refDate = new Date(Date.UTC(refYear, refMonth - 1, refDay));

    // How many days from the reference date to the current week start?
    const diffDays = Math.round(
      (weekStart.getTime() - refDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    // Which fortnight index (can be negative if today is before refDate)?
    const fortnightIndex = Math.floor(diffDays / 14);

    const fortnightStart = new Date(refDate);
    fortnightStart.setUTCDate(refDate.getUTCDate() + fortnightIndex * 14);

    const fortnightEnd = new Date(fortnightStart);
    fortnightEnd.setUTCDate(fortnightStart.getUTCDate() + 13);

    return {
      startDate: fortnightStart.toISOString().slice(0, 10),
      endDate: fortnightEnd.toISOString().slice(0, 10),
    };
  }

  // Fallback: 2-week window starting from the current pay-week start
  const fortnightEnd = new Date(weekStart);
  fortnightEnd.setUTCDate(weekStart.getUTCDate() + 13);
  return {
    startDate: weekStart.toISOString().slice(0, 10),
    endDate: fortnightEnd.toISOString().slice(0, 10),
  };
}
